import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFilterActions, useFilterState } from "../filter";

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface DataTablePaginationProps {
  pagination: Pagination;
}

export function DataTablePagination({ pagination }: DataTablePaginationProps) {
  const filters = useFilterState();
  const { updateFilters } = useFilterActions();

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {pagination.total} loja(s) participante(s).
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Lojas por página</p>
          <Select
            value={`${filters.limit}`}
            onValueChange={(value) => {
              updateFilters({ limit: value, page: "1" });
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={filters.limit} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 50, 100].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Página {pagination.page} de {pagination.totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => updateFilters({ page: "1" })}
            disabled={pagination.page === 1}
          >
            <span className="sr-only">Ir para a primeira página</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() =>
              updateFilters({ page: Math.max(1, pagination.page - 1).toString() })
            }
            disabled={pagination.page === 1}
          >
            <span className="sr-only">Ir para a página anterior</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() =>
              updateFilters({
                page: Math.min(
                  pagination.totalPages,
                  pagination.page + 1
                ).toString(),
              })
            }
            disabled={pagination.page >= pagination.totalPages}
          >
            <span className="sr-only">Ir para a próxima página</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() =>
              updateFilters({ page: pagination.totalPages.toString() })
            }
            disabled={pagination.page >= pagination.totalPages}
          >
            <span className="sr-only">Ir para a última página</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
